import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResetPassword } from '../../../helpers/api/users/users';
import { Form, Input } from 'antd';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import {
  extractPasswordValidationError,
  validatePasswordStrength,
} from '../../../helpers/passwordValidation';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Get code and email from URL params
  const code = searchParams.get('code') || '';
  const email = searchParams.get('email') || '';

  const { mutate: resetPassword, isPending } = useResetPassword({
    onSuccess: () => {
      navigate('/login');
      form.resetFields();
    },
    onError: (err: any) => {
      const passwordError = extractPasswordValidationError(err);
      if (passwordError) {
        form.setFields([{ name: 'password', errors: [passwordError] }]);
        setError(null);
        return;
      }

      setError(err.message || 'An error occurred while resetting password');
    },
  });

  const handleSubmit = () => {
    const passwordError = validatePasswordStrength(password);
    if (passwordError) {
      form.setFields([{ name: 'password', errors: [passwordError] }]);
      setError(null);
      return;
    }

    if (password !== confirmPassword) {
      form.setFields([
        { name: 'confirmPassword', errors: ['Passwords do not match'] },
      ]);
      setError('Passwords do not match');
      return;
    }

    form.setFields([
      { name: 'password', errors: [] },
      { name: 'confirmPassword', errors: [] },
    ]);
    setError(null);
    resetPassword({
      email,
      code,
      new_password: password,
    });
  };
  useDocumentTitle('DEEPECHO - Reset Password');

  return (
    <div className="flex h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Reset Password
          </h2>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-500">
            {error}
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-8 space-y-6 bg-white p-8 shadow rounded-lg"
        >
          <Form.Item label="Email">
            <Input value={email} disabled className="text-gray-600" />
          </Form.Item>

          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password' },
            ]}
          >
            <Input.Password
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                form.setFields([{ name: 'password', errors: [] }]);
              }}
              placeholder="Enter new password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              { required: true, message: 'Please confirm your password' },
            ]}
          >
            <Input.Password
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                form.setFields([{ name: 'confirmPassword', errors: [] }]);
              }}
              placeholder="Confirm new password"
            />
          </Form.Item>

          <Form.Item>
            <button
              type="submit"
              disabled={isPending}
              className={`w-full rounded-md bg-blue-600 py-2 px-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isPending ? 'cursor-not-allowed opacity-70' : ''
              }`}
            >
              {isPending ? 'Resetting...' : 'Reset Password'}
            </button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default ResetPassword;
